import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const KeywordMatched = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'Keyword Matched in SMS' }
  ]

  const useCases = [
    {
      title: "Automatic STOP Request Compliance for Australian Businesses",
      industry: "Marketing & Compliance",
      description: "Automatically handle opt-out requests (STOP, UNSUBSCRIBE) to maintain ACMA compliance and avoid penalties. Process requests instantly without manual intervention.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Customer sends SMS: 'STOP' to your Australian marketing number",
        "Keyword 'STOP' is detected (case-insensitive matching)",
        "Immediately add phone number to suppression list in Google Sheets/Airtable",
        "Update subscriber status to 'unsubscribed' in Mailchimp/ActiveCampaign",
        "Create compliance log entry with timestamp and customer details",
        "Send confirmation SMS: 'You have been unsubscribed. Reply START to opt back in.'",
        "Notify marketing team via Slack: 'Customer +61412345678 opted out'"
      ],
      keywords: "SMS opt-out automation Australia, STOP request handling, ACMA compliance SMS, SMS unsubscribe automation Australia"
    },
    {
      title: "Customer Loyalty Program via SMS Commands",
      industry: "Retail & Hospitality",
      description: "Let customers check points balance, redeem rewards, and access exclusive offers by texting simple keywords to your Australian business number.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Customer sends SMS: 'POINTS' to check loyalty balance",
        "Keyword 'POINTS' triggers workflow",
        "Look up customer phone number in loyalty database (via Zapier Tables/Airtable)",
        "Retrieve current points balance: 2,450 points",
        "Send SMS reply: 'Hi Sarah! You have 2,450 points. Text REDEEM to see rewards or OFFER for today's exclusive deal.'",
        "Log interaction in CRM with timestamp",
        "If keyword is 'REDEEM', send list of available rewards",
        "If keyword is 'OFFER', send personalised discount code valid for 24 hours"
      ],
      keywords: "SMS loyalty program Australia, keyword-based rewards, SMS commands Australia, customer engagement automation"
    },
    {
      title: "Event Registration and Check-In via SMS",
      industry: "Events & Entertainment",
      description: "Allow attendees to register for events, receive tickets, and check in using simple SMS keywords. Perfect for Australian festivals, conferences, and sporting events.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Promote event: 'Text FESTIVAL to 0400123456 to register'",
        "Attendee sends SMS: 'FESTIVAL'",
        "Keyword 'FESTIVAL' triggers registration workflow",
        "Check if phone number already registered in Eventbrite/Google Sheets",
        "If new: Create attendee record, generate unique ticket ID",
        "Send SMS with ticket: 'Registered! Your ticket ID: #TKT-7834. Show this at entry. Event details: [link]'",
        "If existing: Send SMS: 'You're already registered! Ticket ID: #TKT-7834'",
        "Additional keywords: 'SCHEDULE' for timetable, 'MAP' for venue layout, 'PARKING' for directions"
      ],
      keywords: "SMS event registration Australia, SMS ticketing system, event check-in automation, SMS RSVP Australia"
    },
    {
      title: "Automated Help Desk with SMS Commands",
      industry: "Customer Support & IT",
      description: "Provide instant support by letting customers text keywords like HELP, STATUS, TRACK to get automated responses and create support tickets for Australian businesses.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Customer sends SMS: 'HELP'",
        "Keyword 'HELP' triggers help desk workflow",
        "Send automated SMS menu: '1. TRACK your order 2. RETURNS info 3. BILLING questions 4. SUPPORT ticket. Reply with keyword.'",
        "If customer replies 'TRACK': Look up recent order by phone number in Shopify/WooCommerce",
        "Send order status: 'Order #1234 shipped yesterday. Track: [link]. Delivery: Wed 15th.'",
        "If customer replies 'SUPPORT': Create Zendesk/Freshdesk ticket automatically",
        "Send SMS: 'Ticket #56789 created. Agent will respond within 2 hours. Reply here for questions.'",
        "Route ticket to appropriate team based on keyword (BILLING → Finance, TECH → IT)"
      ],
      keywords: "SMS help desk automation, SMS customer support Australia, automated SMS responses, SMS ticket system Australia"
    },
    {
      title: "SMS Contest and Competition Entry System",
      industry: "Marketing & Promotions",
      description: "Run SMS competitions where customers enter by texting a keyword. Automatically validate entries, pick winners, and notify participants. Fully compliant with Australian competition laws.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Run radio/social promotion: 'Win $10,000! Text WIN to 0400123456. T&Cs apply.'",
        "Listener sends SMS: 'WIN'",
        "Keyword 'WIN' triggers competition entry workflow",
        "Validate entry: Check if phone number already entered (1 entry per person rule)",
        "If new entry: Add to Google Sheets with timestamp, name, phone number",
        "Send confirmation SMS: 'You're entered! Winner announced Friday 8pm. Good luck!'",
        "If duplicate entry: Send SMS: 'You're already entered! Only one entry per person.'",
        "Send T&Cs link in follow-up SMS (NSW gambling compliance)",
        "At competition end: Use Zapier formatter to pick random winner from sheet",
        "Send winner notification SMS with prize claim instructions"
      ],
      keywords: "SMS competition Australia, SMS contest entry, competition automation Australia, SMS prize draw"
    },
    {
      title: "Service Appointment Management via SMS",
      industry: "Healthcare & Professional Services",
      description: "Let Australian patients and clients manage appointments by texting keywords. Reduce no-shows, confirm bookings, and reschedule—all via automated SMS commands.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Send appointment reminder 48 hours before: 'Appointment Wed 2pm. Reply CONFIRM or CANCEL'",
        "Patient sends SMS: 'CONFIRM'",
        "Keyword 'CONFIRM' triggers confirmation workflow",
        "Update appointment status in Acuity Scheduling/Calendly to 'confirmed'",
        "Send SMS: 'Confirmed! See you Wed 2pm at 123 Smith St, Sydney. Reply CANCEL to cancel anytime.'",
        "If patient replies 'CANCEL': Mark appointment as cancelled in booking system",
        "Send SMS: 'Appointment cancelled. Reply RESCHEDULE for new times or call 02 1234 5678.'",
        "If keyword 'RESCHEDULE': Send available time slots via SMS",
        "Track confirmation rates in Google Sheets for reporting"
      ],
      keywords: "SMS appointment confirmation Australia, medical appointment SMS automation, SMS booking system Australia"
    },
    {
      title: "Subscription Management and Billing Alerts",
      industry: "SaaS & Subscription Services",
      description: "Allow Australian subscribers to manage their accounts via SMS commands. Handle upgrades, downgrades, pause requests, and payment issues through simple keyword triggers.",
      trigger: "DataFlows SMS - Keyword Matched in SMS",
      steps: [
        "Customer receives SMS: 'Payment failed for your subscription. Reply RETRY to update card or PAUSE to pause account.'",
        "Customer sends SMS: 'RETRY'",
        "Keyword 'RETRY' triggers payment workflow",
        "Send Stripe payment link via SMS: 'Update your card: [secure link]. We'll retry in 24 hours.'",
        "Log interaction in Stripe/ChargeBee customer record",
        "If keyword is 'PAUSE': Update subscription status to 'paused' in billing system",
        "Send SMS: 'Account paused for 30 days. Reply RESUME anytime. No charges until then.'",
        "Additional keywords: 'UPGRADE' for premium plans, 'CANCEL' for cancellation flow",
        "Create retention workflow: If 'CANCEL' detected, offer discount code to retain customer"
      ],
      keywords: "SMS subscription management Australia, billing SMS automation, SMS payment reminders, subscription SMS commands Australia"
    }
  ]

  const faqItems = [
    {
      question: "How does keyword matching work for SMS messages in Australia?",
      answer: "Keyword matching is case-insensitive and matches exact words or phrases in incoming SMS messages. You can set up multiple keywords (STOP, HELP, INFO) and even multi-word phrases ('CHECK BALANCE'). The trigger fires instantly when DataFlows SMS detects your specified keyword anywhere in the message. For example, 'STOP' will match 'stop', 'STOP', or 'Stop sending me messages' because 'STOP' appears in the text."
    },
    {
      question: "Can I use the same keyword for different workflows in Zapier?",
      answer: "Yes! You can create multiple Zaps with the same keyword trigger. Each Zap will run independently when the keyword is matched. This is useful for logging to different systems (e.g., one Zap logs to Google Sheets, another creates a CRM record, and a third sends a Slack notification—all triggered by the same 'STOP' keyword)."
    },
    {
      question: "Is keyword matching ACMA compliant for Australian SMS marketing?",
      answer: "Absolutely. The Keyword Matched trigger is essential for ACMA compliance. Australian law requires businesses to provide a clear opt-out mechanism (typically 'STOP' or 'UNSUBSCRIBE'). With DataFlows SMS and Zapier, you can automatically process these requests within seconds, maintaining compliance and avoiding penalties. We recommend confirming the opt-out with a final SMS as per ACMA guidelines."
    },
    {
      question: "What happens if a message contains multiple keywords?",
      answer: "If an SMS contains multiple keywords you're monitoring (e.g., 'STOP HELP'), both triggers will fire, and each associated Zap will run. You can use Zapier Filters to handle priority (e.g., always process 'STOP' first, ignore other keywords). We recommend designing your keyword strategy to avoid conflicts—use distinct, unique keywords for different purposes."
    },
    {
      question: "Can I use partial keyword matching or wildcards?",
      answer: "The Keyword Matched trigger uses exact word matching, not partial matching. 'STOP' will match only the word 'STOP', not 'STOPPING' or 'UNSTOPPABLE'. For more complex matching (regex, partial matches, or multiple variations), we recommend using the 'New SMS Received' trigger followed by Zapier Formatter to extract and match patterns using custom logic."
    },
    {
      question: "How fast is the keyword detection and workflow execution?",
      answer: "Instant! DataFlows SMS uses webhooks (not polling), so the moment an SMS with your keyword arrives, Zapier is notified within 1-2 seconds. The entire workflow—from keyword detection to automated response—typically completes in under 5 seconds. This is crucial for time-sensitive use cases like STOP requests or customer support commands."
    },
    {
      question: "Can I track which keywords are most popular with Australian customers?",
      answer: "Yes! Every time the Keyword Matched trigger fires, you can log the event to Google Sheets, Airtable, or your analytics platform. Track metrics like: keyword used, customer phone number, timestamp, and message content. This helps you understand which commands customers use most and optimise your keyword strategy accordingly."
    },
    {
      question: "What's the best practice for keyword naming in Australia?",
      answer: "Use simple, memorable, single-word keywords in UPPERCASE: STOP, HELP, START, INFO, JOIN, WIN. Avoid: complex phrases, numbers (unless necessary), or ambiguous words. Always promote keywords clearly: 'Text STOP to 0400123456 to unsubscribe'. Test keywords with real users before launching. For loyalty programs, use intuitive commands: BALANCE, REWARDS, POINTS, REDEEM."
    },
    {
      question: "Can I automatically reply with different messages based on the keyword?",
      answer: "Absolutely! Each keyword trigger can have its own Zap with customised actions. For example: 'STOP' → Send unsubscribe confirmation. 'HELP' → Send help menu. 'BALANCE' → Look up and send loyalty points. You can use Zapier's 'Send SMS' action to reply automatically with contextual messages based on which keyword was detected."
    },
    {
      question: "How do I handle typos or keyword variations like 'STPO' instead of 'STOP'?",
      answer: "You have two options: 1) Create separate Zaps for common typos ('STOP', 'STPO', 'SOTP'), or 2) Use the 'New SMS Received' trigger with Zapier Formatter to calculate string similarity (fuzzy matching) and catch typos. For critical keywords like 'STOP', we recommend monitoring common typos to ensure no opt-out requests are missed due to spelling errors."
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>Keyword Matched in SMS - DataFlows Zapier | SMS Keyword Automation Australia</title>
        <meta
          name="description"
          content="Automate SMS keyword triggers for Australian businesses with DataFlows and Zapier. Handle STOP requests, loyalty programs, event registration, help desk commands. Instant webhooks, ACMA compliant, 6,000+ app integrations."
        />
        <meta name="keywords" content="SMS keyword automation Australia, SMS commands Australia, keyword trigger SMS Australia, SMS STOP automation, SMS loyalty program Australia, SMS help desk automation, ACMA compliant SMS, SMS competition Australia, keyword-based SMS automation" />
        <meta property="og:title" content="SMS Keyword Automation for Australian Businesses - DataFlows Zapier" />
        <meta property="og:description" content="Trigger automations when customers text keywords like STOP, HELP, or custom commands. Perfect for compliance, loyalty programs, events, and customer support." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/triggers/keyword-matched')} />
      </Helmet>

      <div className="feature-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="feature-hero" data-aos="fade-up">
        <div className="container">
          <div className="hero-badge">🔍 Smart Trigger</div>
          <h1>Keyword Matched in SMS</h1>
          <p className="hero-subtitle">
            Automatically trigger workflows when customers text specific keywords or phrases to your Australian business. Handle STOP requests for ACMA compliance, power loyalty programs, manage events, automate support—all through simple SMS commands your customers already know.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">&lt;2s</div>
              <div className="stat-label">Trigger Speed</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">100%</div>
              <div className="stat-label">ACMA Compliant</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">Unlimited</div>
              <div className="stat-label">Keywords</div>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-overview" data-aos="fade-up">
        <div className="container">
          <h2>What Is SMS Keyword Matching?</h2>
          <p className="section-intro">
            SMS keyword matching automatically detects when customers send specific words or phrases to your Australian mobile number, instantly triggering automated workflows. Instead of manually monitoring every incoming SMS for commands like "STOP", "HELP", or "BALANCE", DataFlows SMS detects these keywords in real-time and connects to Zapier to execute your custom automation—whether that's updating a database, sending a reply, or creating a support ticket.
          </p>

          <div className="overview-grid">
            <div className="overview-card">
              <div className="card-icon">🚫</div>
              <h3>Compliance Keywords</h3>
              <p>Handle legally required opt-out commands instantly. STOP, UNSUBSCRIBE, CANCEL, OPT-OUT, and REMOVE are automatically processed to maintain ACMA compliance and avoid penalties.</p>
              <div className="emotion-tags">
                <span>STOP</span>
                <span>UNSUBSCRIBE</span>
                <span>CANCEL</span>
                <span>OPT-OUT</span>
                <span>REMOVE</span>
                <span>END</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">❓</div>
              <h3>Support Keywords</h3>
              <p>Provide instant help and information through automated responses. Guide customers to resources, create tickets, or route to appropriate teams based on their SMS commands.</p>
              <div className="emotion-tags negative">
                <span>HELP</span>
                <span>INFO</span>
                <span>SUPPORT</span>
                <span>STATUS</span>
                <span>TRACK</span>
                <span>FAQ</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">🎁</div>
              <h3>Custom Keywords</h3>
              <p>Create unique commands for your business. Loyalty programs (POINTS, REDEEM), events (REGISTER, TICKETS), competitions (WIN, ENTER), or any custom workflow you need.</p>
              <div className="emotion-tags neutral">
                <span>POINTS</span>
                <span>BALANCE</span>
                <span>WIN</span>
                <span>JOIN</span>
                <span>REGISTER</span>
                <span>CONFIRM</span>
              </div>
            </div>
          </div>

          <div className="key-benefits" style={{ marginTop: '3rem' }}>
            <h3>Why Australian Businesses Choose Keyword-Based SMS Automation</h3>
            <div className="benefits-list">
              <div className="benefit-point">
                <strong>Instant Compliance:</strong> Automatically process STOP requests within seconds to meet ACMA requirements. No manual intervention, no delays, no compliance risks.
              </div>
              <div className="benefit-point">
                <strong>Customer Convenience:</strong> Australians are familiar with SMS commands from banking alerts and service providers. Leverage this familiarity for your business.
              </div>
              <div className="benefit-point">
                <strong>Reduce Support Load:</strong> Common questions answered automatically via keywords. Customers get instant responses without waiting for human agents.
              </div>
              <div className="benefit-point">
                <strong>Scale Without Limits:</strong> Whether you receive 10 or 10,000 SMS commands per day, automation handles every message consistently and instantly.
              </div>
              <div className="benefit-point">
                <strong>No App Required:</strong> Unlike mobile apps that require downloads and updates, SMS works on every mobile phone in Australia. 100% reach, zero barriers.
              </div>
              <div className="benefit-point">
                <strong>Flexible Implementation:</strong> Monitor single keywords, multiple keywords, or entire phrases. Create simple or complex workflows based on your business needs.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works" data-aos="fade-up">
        <div className="container">
          <h2>How Keyword Matching Works in Zapier</h2>
          <p className="section-intro">
            DataFlows SMS detects keywords in real-time and triggers your Zapier workflows instantly. No polling delays, no manual checking—just automated responses the moment your keyword is detected.
          </p>

          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Customer Sends SMS with Keyword</h3>
                <p>A customer texts your Australian mobile number with a message containing your monitored keyword.</p>
                <div className="code-example">
                  From: +61412345678<br/>
                  To: +61400123456<br/>
                  Message: "STOP sending me messages"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>DataFlows Detects Keyword</h3>
                <p>DataFlows SMS platform receives the message and instantly scans for your configured keywords. Matching is case-insensitive and word-based.</p>
                <div className="code-example">
                  Keyword detected: "STOP"<br/>
                  Match type: Exact word match<br/>
                  Position: Beginning of message<br/>
                  Confidence: 100%
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Zapier Workflow Triggered</h3>
                <p>The Keyword Matched trigger fires your Zap via webhook (no polling delay). All message data is passed to Zapier for your workflow.</p>
                <div className="code-example">
                  trigger: "Keyword Matched in SMS"<br/>
                  keyword: "STOP"<br/>
                  from: "+61412345678"<br/>
                  to: "+61400123456"<br/>
                  message: "STOP sending me messages"<br/>
                  timestamp: "2025-11-10T14:32:18+10:00"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Execute Your Automation</h3>
                <p>Zapier runs your custom workflow based on the keyword detected.</p>
                <ul>
                  <li><strong>STOP:</strong> Add to suppression list, send confirmation SMS</li>
                  <li><strong>HELP:</strong> Send help menu, create support ticket</li>
                  <li><strong>BALANCE:</strong> Look up points, send SMS with balance</li>
                  <li><strong>Custom:</strong> Any workflow you've configured</li>
                </ul>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Customer Receives Response</h3>
                <p>Within seconds, your customer receives an automated SMS response relevant to their keyword command.</p>
                <div className="code-example">
                  To: +61412345678<br/>
                  From: +61400123456<br/>
                  Message: "You have been unsubscribed from our marketing messages. Reply START anytime to opt back in. Questions? Call 1300 123 456."
                </div>
              </div>
            </div>
          </div>

          <div className="technical-details" style={{ marginTop: '3rem', padding: '2rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3>Technical Details: How Keyword Detection Works</h3>
            <ul style={{ lineHeight: '1.8' }}>
              <li><strong>Case-Insensitive:</strong> 'STOP', 'stop', 'Stop', and 'StoP' all match the keyword 'STOP'</li>
              <li><strong>Word Boundaries:</strong> 'STOP' matches in "STOP messages" but not in "UNSTOPPABLE"</li>
              <li><strong>Position Flexible:</strong> Keyword can appear anywhere: beginning, middle, or end of message</li>
              <li><strong>Multi-Word Support:</strong> Match phrases like "CHECK BALANCE" or "OPT OUT"</li>
              <li><strong>Unicode Support:</strong> Works with emojis and special characters (e.g., "STOP ❌" still matches "STOP")</li>
              <li><strong>Instant Webhooks:</strong> Trigger fires within 1-2 seconds (vs 2-15 minutes with polling-based triggers)</li>
              <li><strong>No Duplicates:</strong> Each SMS triggers only once, even if it contains multiple instances of the same keyword</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="use-cases-section" data-aos="fade-up">
        <div className="container">
          <h2>Real-World Use Cases for Australian Businesses</h2>
          <p className="section-intro">
            See how Australian businesses across industries use keyword-triggered SMS automation to improve compliance, engage customers, reduce costs, and scale operations without additional staff.
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-detailed" data-aos="fade-up">
              <div className="use-case-header">
                <div>
                  <h3>{useCase.title}</h3>
                  <span className="industry-tag">{useCase.industry}</span>
                </div>
              </div>
              <p className="use-case-description">{useCase.description}</p>

              <div className="workflow-breakdown">
                <h4>Workflow Steps:</h4>
                <ol>
                  {useCase.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>

            </div>
          ))}
        </div>
      </section>

      <section className="australian-focus" data-aos="fade-up">
        <div className="container">
          <h2>Why Australian Businesses Choose DataFlows SMS Keyword Automation</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>🇦🇺 Australian Phone Numbers</h3>
              <p>Native Australian mobile numbers (04XX XXX XXX) and dedicated short codes. Customers text local numbers they trust, not international or unfamiliar numbers.</p>
            </div>
            <div className="benefit-item">
              <h3>⚖️ ACMA Compliance Built-In</h3>
              <p>Automatically handle STOP, UNSUBSCRIBE, and other opt-out keywords as required by Australian Communications and Media Authority (ACMA) regulations. Avoid penalties and maintain trust.</p>
            </div>
            <div className="benefit-item">
              <h3>⚡ Instant Webhook Triggers</h3>
              <p>No polling delays. The moment a keyword is detected, your Zapier workflow fires. Critical for time-sensitive use cases like STOP requests (legally must be processed "promptly") and customer support.</p>
            </div>
            <div className="benefit-item">
              <h3>💰 Cost-Effective for Aussie SMBs</h3>
              <p>Pay only for SMS sent/received. No per-keyword charges, no per-trigger fees. Perfect for Australian small businesses with tight budgets but big ambitions.</p>
            </div>
            <div className="benefit-item">
              <h3>🔗 6,000+ App Integrations</h3>
              <p>Connect keywords to any app in the Zapier ecosystem: Google Sheets, Airtable, Shopify, Zendesk, Slack, HubSpot, Mailchimp, Salesforce, and thousands more. Build exactly the workflow your business needs.</p>
            </div>
            <div className="benefit-item">
              <h3>📊 Analytics and Reporting</h3>
              <p>Track which keywords are used most, when they're used, and by whom. Identify trends, optimise your keyword strategy, and measure ROI of SMS automation initiatives.</p>
            </div>
            <div className="benefit-item">
              <h3>🔒 Secure and Private</h3>
              <p>Fully compliant with Australian Privacy Act 1988. Customer data is encrypted in transit and at rest. No data shared with third parties. GDPR and SOC 2 certified.</p>
            </div>
            <div className="benefit-item">
              <h3>🛠️ Easy Setup, No Coding</h3>
              <p>Configure keyword triggers through Zapier's visual interface. No technical skills required. Australian business owners, marketers, and support teams can build automations without IT help.</p>
            </div>
            <div className="benefit-item">
              <h3>📱 Works on All Devices</h3>
              <p>SMS works on every mobile phone in Australia—smartphones, feature phones, old Nokia bricks. No app downloads, no internet required. 100% reach across all demographics.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="advanced-strategies" data-aos="fade-up" style={{ background: '#f8f9fa', padding: '4rem 0' }}>
        <div className="container">
          <h2>Advanced Keyword Automation Strategies for Australian Markets</h2>
          <p className="section-intro">
            Take your SMS keyword automation to the next level with these proven strategies from successful Australian businesses.
          </p>

          <div className="strategy-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Multi-Keyword Conversations</h3>
              <p>Create conversational flows where one keyword leads to another. Example: HELP → menu → BILLING → billing options → DISPUTE → create ticket.</p>
              <p><strong>Implementation:</strong> Use Zapier's "Update Contact" action to store conversation state, then reference it in subsequent triggers.</p>
            </div>

            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Smart Keyword Suggestions</h3>
              <p>When customers send unrecognised text, analyse it and suggest relevant keywords. "Looking for help? Try: HELP for support, TRACK for orders, BILLING for payment questions."</p>
              <p><strong>Implementation:</strong> Use "New SMS Received" trigger + Zapier AI to analyse intent + Send SMS with suggestions.</p>
            </div>

            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Personalised Keyword Responses</h3>
              <p>Tailor automated responses based on customer data. VIP customers get priority routing, repeat customers get special offers, new customers get onboarding.</p>
              <p><strong>Implementation:</strong> Look up phone number in CRM when keyword triggers, then use Zapier Paths to route based on customer segment.</p>
            </div>

            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Time-Based Keyword Behaviour</h3>
              <p>Same keyword, different action based on time. HELP during business hours creates live chat, after hours creates ticket for tomorrow.</p>
              <p><strong>Implementation:</strong> Add Zapier Filter checking current time (AEST/AEDT), then use Paths for different workflows.</p>
            </div>

            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Progressive Profiling via Keywords</h3>
              <p>Collect customer data over time through keyword interactions. WIN → ask name, REGISTER → ask email, BIRTHDAY → ask birthdate for future campaigns.</p>
              <p><strong>Implementation:</strong> Store partial customer profiles in Airtable/Google Sheets, update with each keyword interaction.</p>
            </div>

            <div className="strategy-card" style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3>Gamified Keyword Campaigns</h3>
              <p>Create scavenger hunts, trivia games, or challenges using keywords. Each correct answer (keyword) unlocks the next clue. Great for events, launches, brand engagement.</p>
              <p><strong>Implementation:</strong> Track progress in Google Sheets, use Zapier Filters to verify correct keywords in sequence, send next clue via SMS.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="setup-guide" data-aos="fade-up">
        <div className="container">
          <h2>How to Set Up Keyword Matching in Zapier</h2>

          <div className="setup-step">
            <h3>Step 1: Create Your Zap</h3>
            <ol>
              <li>Log in to <a href="https://zapier.com" target="_blank" rel="noopener noreferrer">Zapier</a></li>
              <li>Click "Create Zap"</li>
              <li>Search for "DataFlows SMS" and select it as your trigger app</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 2: Choose Keyword Matched Trigger</h3>
            <ol>
              <li>Select trigger event: <strong>"Keyword Matched in SMS"</strong></li>
              <li>Connect your DataFlows SMS account using your API token</li>
              <li>Get your API token from: <a href="https://sms.dataflows.com.au/developers/api" target="_blank" rel="noopener noreferrer">https://sms.dataflows.com.au/developers/api</a></li>
              <li>Click "Continue"</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 3: Configure Your Keyword</h3>
            <ol>
              <li>In the "Keyword" field, enter the word or phrase you want to monitor</li>
              <li>Examples: <code>STOP</code>, <code>HELP</code>, <code>CHECK BALANCE</code>, <code>REGISTER</code></li>
              <li>Keywords are case-insensitive (STOP = stop = Stop)</li>
              <li>For multi-word phrases, use exact spacing: <code>CHECK BALANCE</code> not <code>CHECKBALANCE</code></li>
              <li><strong>Pro tip:</strong> Create separate Zaps for each important keyword to keep workflows organised</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 4: Test the Trigger</h3>
            <ol>
              <li>Send a test SMS to your DataFlows number containing the keyword</li>
              <li>Example: Text "STOP" to your Australian mobile number</li>
              <li>Click "Test trigger" in Zapier</li>
              <li>Verify that Zapier detected the SMS with your keyword</li>
              <li>Check that all data fields are populated: from number, to number, message, timestamp</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 5: Add Your Actions</h3>
            <ol>
              <li>Click "+" to add an action step</li>
              <li>Choose what should happen when the keyword is detected</li>
              <li><strong>Common actions for STOP:</strong>
                <ul>
                  <li>Add row to Google Sheets (suppression list)</li>
                  <li>Update contact in Mailchimp (unsubscribe)</li>
                  <li>Send SMS via DataFlows (confirmation)</li>
                  <li>Send notification to Slack (alert team)</li>
                </ul>
              </li>
              <li><strong>Common actions for HELP:</strong>
                <ul>
                  <li>Send SMS with help menu</li>
                  <li>Create ticket in Zendesk/Freshdesk</li>
                  <li>Log to Google Sheets for tracking</li>
                </ul>
              </li>
              <li><strong>Common actions for custom keywords:</strong>
                <ul>
                  <li>Look up data in Airtable/database</li>
                  <li>Send personalised SMS response</li>
                  <li>Update CRM record</li>
                  <li>Trigger email sequence</li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 6: Configure STOP Compliance (Essential for Australian Businesses)</h3>
            <ol>
              <li>Create a Zap specifically for the keyword: <code>STOP</code></li>
              <li><strong>Action 1:</strong> Add phone number to suppression list (Google Sheets/Airtable/CRM)</li>
              <li><strong>Action 2:</strong> Update subscriber status in marketing platform (Mailchimp/ActiveCampaign) to "unsubscribed"</li>
              <li><strong>Action 3:</strong> Send confirmation SMS: "You have been unsubscribed. Reply START to opt back in."</li>
              <li><strong>Action 4:</strong> Create compliance log entry with timestamp, number, and action taken</li>
              <li><strong>Pro tip:</strong> Also monitor variations: UNSUBSCRIBE, CANCEL, OPT-OUT, REMOVE, END</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 7: Test End-to-End</h3>
            <ol>
              <li>Send real SMS messages from your mobile phone to your DataFlows number</li>
              <li>Test each keyword you've configured</li>
              <li>Verify that:
                <ul>
                  <li>Trigger fires within 2-3 seconds</li>
                  <li>All actions execute correctly</li>
                  <li>Automated SMS replies are received</li>
                  <li>Data is logged to sheets/CRM</li>
                </ul>
              </li>
              <li>Test edge cases: keyword in middle of sentence, all caps, all lowercase, with emojis</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 8: Turn On Your Zap</h3>
            <ol>
              <li>Review all steps one final time</li>
              <li>Click "Turn on Zap"</li>
              <li>Your keyword automation is now live!</li>
              <li>Monitor the first few real triggers in Zapier's "Zap History" to ensure everything works</li>
              <li>Set up email/Slack alerts for Zap errors so you're notified immediately if something breaks</li>
            </ol>
          </div>

          <div className="setup-tip" style={{ marginTop: '3rem', padding: '2rem', background: '#e7f3ff', borderLeft: '4px solid #0066cc', borderRadius: '4px' }}>
            <h4>💡 Pro Setup Tips for Australian Businesses</h4>
            <ul style={{ lineHeight: '1.8', marginTop: '1rem' }}>
              <li><strong>Priority keywords first:</strong> Set up STOP, HELP, and compliance keywords before marketing/engagement keywords</li>
              <li><strong>Use Zapier Folders:</strong> Organise keyword Zaps into folders: "Compliance", "Support", "Marketing", "Events"</li>
              <li><strong>Monitor Zap performance:</strong> Check Zap History weekly to identify failed workflows or errors</li>
              <li><strong>Document your keywords:</strong> Keep a master list of all active keywords, what they do, and which Zap handles them</li>
              <li><strong>Test before promoting:</strong> Always test new keywords thoroughly before including them in marketing materials</li>
              <li><strong>Set up START keyword:</strong> If you offer STOP, also offer START (opt back in) for customer convenience</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="keyword-strategy" data-aos="fade-up" style={{ background: '#fff', padding: '4rem 0' }}>
        <div className="container">
          <h2>Keyword Strategy Guide for Australian Businesses</h2>
          <p className="section-intro">
            Choosing the right keywords and promoting them effectively is crucial for successful SMS automation. Follow these best practices from leading Australian SMS marketers.
          </p>

          <div className="strategy-section" style={{ marginTop: '2rem' }}>
            <h3>Choosing Effective Keywords</h3>
            <div className="strategy-content" style={{ background: '#f8f9fa', padding: '2rem', borderRadius: '8px', marginTop: '1rem' }}>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Keep it short:</strong> Single words are best (STOP, HELP, WIN). Maximum 2-3 words for complex commands (CHECK BALANCE).</li>
                <li><strong>Make it memorable:</strong> Easy to spell, hard to misspell. Avoid: SCHEDULE (often misspelled), QUESTIONNAIRE (too long).</li>
                <li><strong>Use UPPERCASE in promotions:</strong> "Text STOP to 0400123456" is clearer than "text stop to 0400123456".</li>
                <li><strong>Avoid ambiguous words:</strong> Don't use "OK" (customers might send it accidentally). Use "CONFIRM" or "YES" instead.</li>
                <li><strong>Industry conventions:</strong> Stick to familiar keywords where possible: STOP, HELP, JOIN, LEAVE, INFO, MORE.</li>
                <li><strong>Test pronunciation:</strong> If promoting on radio/TV, ensure keyword sounds distinct (JOIN vs JOINT).</li>
              </ul>
            </div>
          </div>

          <div className="strategy-section" style={{ marginTop: '3rem' }}>
            <h3>Essential Keywords for Every Australian Business</h3>
            <div className="essential-keywords" style={{ marginTop: '1rem' }}>
              <div className="keyword-list-group">
                <h4>Compliance (Required by Law)</h4>
                <ul style={{ lineHeight: '1.8' }}>
                  <li><strong>STOP:</strong> Unsubscribe from all marketing messages (ACMA requirement)</li>
                  <li><strong>HELP:</strong> Provide customer support and contact information</li>
                  <li><strong>START:</strong> Opt back in (if customer previously unsubscribed)</li>
                </ul>
              </div>

              <div className="keyword-list-group" style={{ marginTop: '2rem' }}>
                <h4>Customer Support</h4>
                <ul style={{ lineHeight: '1.8' }}>
                  <li><strong>HELP:</strong> Send help menu with available commands</li>
                  <li><strong>INFO:</strong> Provide general business information</li>
                  <li><strong>TRACK:</strong> Check order/delivery status</li>
                  <li><strong>RETURNS:</strong> Get returns policy and process</li>
                  <li><strong>HOURS:</strong> Send opening hours and location</li>
                  <li><strong>CONTACT:</strong> Provide phone/email/address</li>
                </ul>
              </div>

              <div className="keyword-list-group" style={{ marginTop: '2rem' }}>
                <h4>Loyalty & Engagement</h4>
                <ul style={{ lineHeight: '1.8' }}>
                  <li><strong>BALANCE:</strong> Check loyalty points balance</li>
                  <li><strong>POINTS:</strong> View current points and how to earn more</li>
                  <li><strong>REDEEM:</strong> See available rewards and how to claim</li>
                  <li><strong>REWARDS:</strong> List of current reward offerings</li>
                  <li><strong>VIP:</strong> Join VIP program or check VIP status</li>
                </ul>
              </div>

              <div className="keyword-list-group" style={{ marginTop: '2rem' }}>
                <h4>Marketing & Campaigns</h4>
                <ul style={{ lineHeight: '1.8' }}>
                  <li><strong>JOIN:</strong> Subscribe to SMS list</li>
                  <li><strong>OFFER:</strong> Get current special offer or discount code</li>
                  <li><strong>DEAL:</strong> Today's deal or promotion</li>
                  <li><strong>WIN:</strong> Enter competition or prize draw</li>
                  <li><strong>MENU:</strong> View product/service menu</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="strategy-section" style={{ marginTop: '3rem' }}>
            <h3>How to Promote Keywords in Australia</h3>
            <div className="promotion-methods" style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div className="method-card" style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                <h4>Physical Locations</h4>
                <ul style={{ lineHeight: '1.8', marginTop: '0.5rem' }}>
                  <li>Posters: "Text SALE to 0400123456 for 20% off"</li>
                  <li>Table tents in restaurants</li>
                  <li>Receipts: "Join our VIP list! Text JOIN to 0400123456"</li>
                  <li>Business cards with keyword and number</li>
                  <li>Shelf talkers in retail</li>
                </ul>
              </div>

              <div className="method-card" style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                <h4>Digital Channels</h4>
                <ul style={{ lineHeight: '1.8', marginTop: '0.5rem' }}>
                  <li>Website footer: "Text HELP to 0400123456 for support"</li>
                  <li>Social media posts and bio</li>
                  <li>Email signature</li>
                  <li>Online ads (Google, Facebook, Instagram)</li>
                  <li>YouTube video descriptions</li>
                </ul>
              </div>

              <div className="method-card" style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                <h4>Broadcast Media</h4>
                <ul style={{ lineHeight: '1.8', marginTop: '0.5rem' }}>
                  <li>Radio: "Text WIN to zero-four-hundred-one-two-three-four-five-six"</li>
                  <li>TV ads with on-screen keyword</li>
                  <li>Podcast sponsorships</li>
                  <li>Print ads in newspapers/magazines</li>
                  <li>Billboard advertising</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section" data-aos="fade-up">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="comparison-section" data-aos="fade-up" style={{ background: '#f8f9fa', padding: '4rem 0' }}>
        <div className="container">
          <h2>DataFlows SMS vs Other Keyword SMS Solutions in Australia</h2>
          <div className="comparison-table" style={{ overflowX: 'auto', marginTop: '2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ background: '#0066cc', color: 'white' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #004999' }}>Feature</th>
                  <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #004999' }}>DataFlows SMS</th>
                  <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #004999' }}>Traditional SMS Gateway</th>
                  <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #004999' }}>Manual Monitoring</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>Response Time</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>&lt;2 seconds (webhook)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>2-15 minutes (polling)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Hours to days</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>Setup Complexity</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>5-10 minutes (no code)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Hours (coding required)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Immediate (but manual)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>ACMA Compliance</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>Automatic (via Zapier)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Manual setup required</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>High risk of delay</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>Cost for 1,000 triggers/month</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>Only SMS costs</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Platform + API fees</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Staff time ($500+)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>Zapier Integration</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>Native (6,000+ apps)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Limited or none</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>None</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <td style={{ padding: '1rem' }}>Scalability</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>Unlimited keywords/triggers</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Limited by plan</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Not scalable</td>
                </tr>
                <tr>
                  <td style={{ padding: '1rem' }}>24/7 Operation</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#00aa00', fontWeight: 'bold' }}>Yes (automated)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>Yes (automated)</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>No (business hours only)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <h2>Ready to Automate Your SMS Keywords?</h2>
          <p>Connect DataFlows SMS with Zapier and start handling STOP requests, loyalty programs, events, and customer support automatically. No coding required, live in minutes.</p>
          <div className="cta-buttons">
            <a href="https://zapier.com/apps/dataflows-sms" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              Get Started with Zapier
            </a>
            <Link to="/integrations/zapier" className="btn btn-secondary">
              View All Zapier Features
            </Link>
          </div>
          <div className="cta-features" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem' }}>⚡</div>
              <div>Instant Setup</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem' }}>🇦🇺</div>
              <div>Australian Numbers</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem' }}>⚖️</div>
              <div>ACMA Compliant</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem' }}>🔗</div>
              <div>6,000+ Apps</div>
            </div>
          </div>
        </div>
      </section>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default KeywordMatched
